/**
 * ComposedFitWidthFrame — COMPOSED 본문(PagedComposerView 낱장 묶음)을 **컨테이너 폭에 맞춰
 * 축소만** 하는 정적 프레임(핀치/팬 없음). 데스크탑 상세·공유·외부결재처럼 viewer 없이 정적으로
 * 그리는 자리의 공통 래퍼다.
 *
 * 왜 필요한가: rc `PagedComposerView`는 낱장을 고정 px 폭(A4 세로 794 / 가로 1123)으로 그린다.
 * 컨테이너(상세 본문 카드 ≈ 832px)보다 넓은 가로(landscape) 낱장은 `overflow-x-auto`만으로는
 * 부족하다 — 낱장 묶음이 flex column 중앙 정렬이라 **양쪽으로** 넘치고, 왼쪽으로 넘친 부분은
 * 스크롤로도 닿지 않는다(2026-09-14 위험성평가 결과서 실측: 헤더 표 라벨 열 145px 잘림).
 * 세로 794px 템플릿만 있던 시절엔 드러나지 않았다.
 *
 * 규약(legacy `ReportDetailPagedBody`·`ComposedPinchViewport`와 같다):
 *  - fit scale = 실측 컨테이너 폭 / 낱장 폭, **최대 1**(넓으면 확대하지 않는다). 세로 낱장이
 *    컨테이너에 들어가면 scale 1 — transform을 아예 붙이지 않아 종전 렌더와 시각적으로 같다.
 *  - wrapper-shrink: transform은 레이아웃 박스를 줄이지 않으므로 축소본의 실제 치수(자연치수 ×
 *    scale)를 바깥 박스에 되돌려 아래 빈 공간·가로 스크롤을 없앤다.
 *  - 폭은 ResizeObserver로 실측한다(window 폭 가정은 레이아웃마다 틀린다 — ComposedPinchViewport
 *    JSDoc의 2026-08-19 사례).
 *  - rc 낱장 분할(`measureBody`)은 offsetHeight를 쓰므로 이 transform이 분할을 왜곡하지 않는다.
 *
 * 모바일 핀치/팬이 필요한 자리(인증 상세)는 `ComposedPinchViewport`가 담당하고, 그 컴포넌트의
 * 데스크탑 분기가 이 프레임으로 떨어진다.
 */
import type { ReactNode } from 'react';
import { useFitWidthMeasure } from '../../../hooks/useFitWidthMeasure';

interface ComposedFitWidthFrameProps {
  /** 낱장 1장의 렌더 폭(px). rc `pagePx(template.page).w` — A4 세로 794, 가로 1123. */
  pageWidthPx: number;
  /** PagedComposerView 낱장 묶음. */
  children: ReactNode;
}

export function ComposedFitWidthFrame({ pageWidthPx, children }: ComposedFitWidthFrameProps) {
  // 가용 폭은 **우리가 소유한 바깥 노드**(measureRef)에서 잰다(호스트 카드의 padding 안쪽 폭).
  // 폭·자연높이 실측 규약은 `useFitWidthMeasure` JSDoc — ComposedPinchViewport와 한 벌이다.
  const { measureRef, innerRef, fitScale, naturalHeight } = useFitWidthMeasure(pageWidthPx);
  const scaled = fitScale < 1;

  return (
    // overflow-x-auto는 종전 정적 렌더의 것 그대로(scale 1 경로의 안전망 — 폭을 못 잰 경우 등).
    <div ref={measureRef} className="overflow-x-auto">
      {/* 스케일 컨테이너: 축소본의 실제 치수(자연치수 × scale)를 레이아웃에 되돌려준다. scale 1이면
          폭=낱장 폭·높이 auto로 종전 중앙 정렬(margin auto)과 같은 자리다. */}
      <div
        style={{
          width: pageWidthPx * fitScale,
          height: scaled && naturalHeight != null ? naturalHeight * fitScale : undefined,
          margin: '0 auto',
        }}
      >
        <div
          ref={innerRef}
          style={{
            width: pageWidthPx,
            // scale 1에는 transform을 붙이지 않는다 — 불필요한 stacking context를 만들지 않아
            // 세로 낱장(종전 유일 형상) 렌더가 종전과 같다.
            transform: scaled ? `scale(${fitScale})` : undefined,
            transformOrigin: scaled ? 'top left' : undefined,
          }}
        >
          {children}
        </div>
      </div>
    </div>
  );
}

export default ComposedFitWidthFrame;
